import { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getStreamsByFund } from '../../services/firestoreService';
import { resolveUserName, formatStageLabel } from '../../utils/formatters';
import FundList from '../../components/admin/funds/FundList';
import FundForm from '../../components/admin/funds/FundForm';
import Modal from '../../components/shared/UI/Modal';
import Button from '../../components/shared/UI/Button';

// Shows the name and publish date of the active (most recently published) rubric for a fund.
function FundActiveRubric({ fundId }) {
  const [rubric, setRubric] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fundId) { setLoading(false); return; }
    const q = query(
      collection(db, 'rubrics'),
      where('fund_id', '==', fundId),
      where('is_published', '==', true)
    );
    getDocs(q).then(snap => {
      const rubrics = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (!rubrics.length) { setRubric(null); setLoading(false); return; }
      const active = rubrics.reduce((best, r) => {
        const bd = best.published_at?.toDate?.() || new Date(0);
        const rd = r.published_at?.toDate?.() || new Date(0);
        return rd > bd ? r : best;
      });
      setRubric(active);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [fundId]);

  if (loading) return <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Loading…</span>;
  if (!rubric) return <span style={{ fontSize: 14 }}>—</span>;

  const dateStr = rubric.published_at?.toDate
    ? rubric.published_at.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return <span style={{ fontSize: 14 }}>{rubric.name} (Published — {dateStr})</span>;
}

// Displays a URL in a grey box with a Copy link button.
function CopyableUrl({ url, caption = 'Share this link with companies to submit a Pipeline Information Form for this fund.' }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'monospace',
            color: 'var(--color-accent)',
            textDecoration: 'none',
            wordBreak: 'break-all',
          }}
        >
          {url}
        </a>
        <Button size="sm" variant="secondary" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy link'}
        </Button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0 }}>
        {caption}
      </p>
    </div>
  );
}

// Public PIF submission link(s) for a fund.
//
// A fund with streams enabled gets ONE link per stream, each carrying the stream
// in the path (/apply/{fundId}/{streamId}) so submitPIF stamps that stream_id on
// the created deal. Every other fund — streams disabled, or enabled but with none
// configured yet — keeps the single fund-level /apply/{fundId} link unchanged.
function FundSubmissionUrls({ fund }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!fund?.id || fund?.has_streams !== true) { setStreams([]); return; }
    setLoading(true);
    getStreamsByFund(fund.id)
      .then(list => { if (!cancelled) setStreams(list); })
      .catch(() => { if (!cancelled) setStreams([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fund?.id, fund?.has_streams]);

  const headingStyle = {
    fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)',
    textTransform: 'uppercase', marginBottom: 8,
  };
  const perStream = streams.length > 0;

  return (
    <div>
      <p style={headingStyle}>{perStream ? 'Per-stream submission links' : 'Public PIF Form URL'}</p>
      {loading ? (
        <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Loading…</span>
      ) : perStream ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {streams.map(s => {
            const label = s.name || s.short_name || s.id;
            return (
              <div key={s.id}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</p>
                <CopyableUrl
                  url={`${window.location.origin}/apply/${fund.id}/${s.id}`}
                  caption={`Share this link with companies submitting to ${label}.`}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <CopyableUrl url={`${window.location.origin}/apply/${fund.id}`} />
      )}
    </div>
  );
}

// Resolves changed_by UID to name for legacy version history entries.
function VersionHistoryEntry({ entry }) {
  const [name, setName] = useState(entry.changed_by_name || null);
  useEffect(() => {
    if (!entry.changed_by || entry.changed_by_name) return;
    resolveUserName(entry.changed_by, db).then(setName);
  }, [entry.changed_by, entry.changed_by_name]);
  return (
    <div className="version-history-item">
      <span style={{ fontWeight: 600, minWidth: 40 }}>v{entry.version}</span>
      <span style={{ color: 'var(--color-text-secondary)' }}>
        Changed {entry.changed_at ? new Date(entry.changed_at).toLocaleDateString() : '—'} by{' '}
        {name || <span style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>Loading…</span>}
      </span>
    </div>
  );
}

function VersionHistoryList({ history }) {
  // Show most recent version first
  const sorted = [...history].sort((a, b) => (b.version || 0) - (a.version || 0));
  return (
    <div>
      <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Version History</p>
      <div className="version-history-list">
        {sorted.map((h, i) => <VersionHistoryEntry key={i} entry={h} />)}
      </div>
    </div>
  );
}

export default function FundsPage() {
  const [view, setView] = useState('list'); // list | add | edit | view
  const [selectedFund, setSelectedFund] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewHistoryOpen, setViewHistoryOpen] = useState(false);

  // Claims refresh state
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsMessage, setClaimsMessage] = useState(null); // { type: 'success'|'error', text: string }

  // Rescore legacy PIFs state
  const [rescoreLoading, setRescoreLoading] = useState(false);
  const [rescoreMessage, setRescoreMessage] = useState(null); // { type: 'success'|'error', text: string }

  // Backfill reviewer names state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState(null); // { type: 'success'|'error', text: string }

  // Repair duplicate question IDs state
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairMessage, setRepairMessage] = useState(null); // { type: 'success'|'error', text: string }

  // Fix 2 Stage C — currency-normalisation rescore (report-first, then apply)
  const [normReportLoading, setNormReportLoading] = useState(false);
  const [normReport, setNormReport] = useState(null); // the dry-run report object
  const [normApplying, setNormApplying] = useState(false);
  const [normMessage, setNormMessage] = useState(null); // { type, text }

  async function handleRefreshClaims() {
    setClaimsLoading(true);
    setClaimsMessage(null);
    try {
      const fns = getFunctions();
      const refreshAll = httpsCallable(fns, 'refreshAllUserClaims');
      const result = await refreshAll();
      const count = result.data?.updated ?? 0;
      setClaimsMessage({
        type: 'success',
        text: `Claims refreshed for ${count} user${count !== 1 ? 's' : ''}. All users must log out and back in.`,
      });
    } catch (err) {
      setClaimsMessage({ type: 'error', text: err.message || 'Failed to refresh claims.' });
    } finally {
      setClaimsLoading(false);
    }
  }

  async function handleRescore() {
    if (!window.confirm(
      'This will recalculate PIF scores for all deals that were scored before the formula fix. ' +
      'This is safe to run — it will not affect deals already using the correct formula. Continue?'
    )) return;
    setRescoreLoading(true);
    setRescoreMessage(null);
    try {
      const fns = getFunctions();
      const rescore = httpsCallable(fns, 'rescoreLegacyPIFs');
      const result = await rescore();
      const { rescored, skipped, errors } = result.data;
      setRescoreMessage({
        type: 'success',
        text: `Done. ${rescored} deals rescored, ${skipped} skipped (already correct), ${errors} errors.`,
      });
    } catch (err) {
      setRescoreMessage({ type: 'error', text: err.message || 'Rescore failed.' });
    } finally {
      setRescoreLoading(false);
    }
  }

  async function handleBackfill() {
    if (!window.confirm(
      'This will look up reviewer names for all existing reviews that are missing them. ' +
      'This is safe to run multiple times. Continue?'
    )) return;
    setBackfillLoading(true);
    setBackfillMessage(null);
    try {
      const fns = getFunctions();
      const backfill = httpsCallable(fns, 'backfillReviewerNames');
      const result = await backfill();
      const { updated, skipped, errors } = result.data;
      setBackfillMessage({
        type: 'success',
        text: `Done. ${updated} reviews updated, ${skipped} skipped (already had names), ${errors} errors.`,
      });
    } catch (err) {
      setBackfillMessage({ type: 'error', text: err.message || 'Backfill failed.' });
    } finally {
      setBackfillLoading(false);
    }
  }

  async function handleRepairQuestionIds() {
    if (!window.confirm(
      'This will scan all rubric documents and fix any duplicate question IDs. ' +
      'Safe to run multiple times. Continue?'
    )) return;
    setRepairLoading(true);
    setRepairMessage(null);
    try {
      const fns = getFunctions();
      const repair = httpsCallable(fns, 'repairDuplicateQuestionIds');
      const result = await repair();
      const { repaired, skipped, errors, details } = result.data;
      const names = details?.length > 0
        ? ' Repaired: ' + details.map(r => r.rubric_name || r.rubric_id).join(', ') + '.'
        : '';
      setRepairMessage({
        type: 'success',
        text: `Done. ${repaired} rubric(s) repaired, ${skipped} skipped (no duplicates), ${errors} errors.${names}`,
      });
    } catch (err) {
      setRepairMessage({ type: 'error', text: err.message || 'Repair failed.' });
    } finally {
      setRepairLoading(false);
    }
  }

  // Fix 2 Stage C — STEP 1: dry-run report (writes nothing)
  async function handleNormRescoreReport() {
    setNormReportLoading(true);
    setNormMessage(null);
    setNormReport(null);
    try {
      const fns = getFunctions();
      const rescore = httpsCallable(fns, 'rescoreForCurrencyNormalisation');
      const result = await rescore({ mode: 'report' });
      setNormReport(result.data);
    } catch (err) {
      setNormMessage({ type: 'error', text: err.message || 'Report failed.' });
    } finally {
      setNormReportLoading(false);
    }
  }

  // Fix 2 Stage C — STEP 2: apply (only reachable after the report is shown)
  async function handleNormRescoreApply() {
    if (!window.confirm(
      'APPLY currency-normalisation rescore now?\n\n' +
      `${normReport?.would_overwrite ?? 0} deal(s) before HoP Review will have their score OVERWRITTEN ` +
      '(a one-time snapshot is preserved first).\n' +
      `${normReport?.would_protect ?? 0} deal(s) at HoP Review or later will NOT be changed — ` +
      'a converted score is stored alongside and they are flagged for review.\n\n' +
      'This is the one operation that rewrites existing scores. Continue?'
    )) return;
    setNormApplying(true);
    setNormMessage(null);
    try {
      const fns = getFunctions();
      const rescore = httpsCallable(fns, 'rescoreForCurrencyNormalisation');
      const result = await rescore({ mode: 'apply' });
      const r = result.data;
      setNormMessage({
        type: 'success',
        text: `Applied. ${r.overwritten} overwritten, ${r.protected_flagged} protected/flagged, ` +
          `${r.snapshots_created} snapshots created, ${r.errors} errors.`,
      });
      setNormReport(result.data); // refresh with post-apply numbers
    } catch (err) {
      setNormMessage({ type: 'error', text: err.message || 'Apply failed.' });
    } finally {
      setNormApplying(false);
    }
  }

  function handleSaved() {
    setView('list');
    setSelectedFund(null);
    setRefreshKey(k => k + 1);
  }

  function handleEdit(fund) {
    setSelectedFund(fund);
    setView('edit');
  }

  function handleView(fund) {
    setSelectedFund(fund);
    setViewHistoryOpen(true);
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Fund Configuration</h1>
          <p>Manage fund details, target metrics and deployment parameters.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            size="sm"
            loading={claimsLoading}
            onClick={handleRefreshClaims}
          >
            Refresh All User Claims
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={rescoreLoading}
            onClick={handleRescore}
          >
            Rescore Legacy PIFs
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={backfillLoading}
            onClick={handleBackfill}
          >
            Backfill Reviewer Names
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={repairLoading}
            onClick={handleRepairQuestionIds}
          >
            Repair Rubric Question IDs
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={normReportLoading}
            onClick={handleNormRescoreReport}
          >
            Rescore for Currency Normalisation
          </Button>
        </div>
      </div>

      {/* Claims refresh result message */}
      {claimsMessage && (
        <div
          className={claimsMessage.type === 'success' ? 'alert alert-success' : 'alert alert-error'}
          style={{ marginBottom: 16 }}
        >
          {claimsMessage.text}
        </div>
      )}

      {/* Rescore result message */}
      {rescoreMessage && (
        <div
          className={rescoreMessage.type === 'success' ? 'alert alert-success' : 'alert alert-error'}
          style={{ marginBottom: 16 }}
        >
          {rescoreMessage.text}
        </div>
      )}

      {/* Backfill reviewer names result message */}
      {backfillMessage && (
        <div
          className={backfillMessage.type === 'success' ? 'alert alert-success' : 'alert alert-error'}
          style={{ marginBottom: 16 }}
        >
          {backfillMessage.text}
        </div>
      )}

      {/* Repair duplicate question IDs result message */}
      {repairMessage && (
        <div
          className={repairMessage.type === 'success' ? 'alert alert-success' : 'alert alert-error'}
          style={{ marginBottom: 16 }}
        >
          {repairMessage.text}
        </div>
      )}

      {/* Fix 2 Stage C — currency-normalisation rescore report + apply panel */}
      {normMessage && (
        <div
          className={normMessage.type === 'success' ? 'alert alert-success' : 'alert alert-error'}
          style={{ marginBottom: 16 }}
        >
          {normMessage.text}
        </div>
      )}
      {normReport && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2>Currency Normalisation Rescore — {normReport.mode === 'apply' ? 'Applied' : 'Dry-run Report'}</h2>
              <p className="text-muted" style={{ fontSize: 12 }}>
                {normReport.candidates} candidate(s): {normReport.would_overwrite} to overwrite,{' '}
                {normReport.would_protect} protected (HoP Review+).{' '}
                Skipped — status: {normReport.skipped_status}, fund off: {normReport.skipped_fund_disabled},{' '}
                no rates: {normReport.skipped_no_rates}, already applied: {normReport.skipped_already_applied}.{' '}
                Errors: {normReport.errors}. Parity mismatches: {normReport.parity_mismatches}.
                {normReport.mode === 'apply' && (
                  <> {' '}· Written: {normReport.overwritten} overwritten, {normReport.protected_flagged} flagged,{' '}
                    {normReport.snapshots_created} snapshots.</>
                )}
              </p>
              {normReport.parity_mismatches > 0 && (
                <div className="alert alert-error" style={{ marginTop: 8, marginBottom: 0 }}>
                  ⚠ {normReport.parity_mismatches} deal(s) FAILED the parity check — the recomputed face-value score
                  does not match the stored score. This means the reconstructed input does not faithfully reproduce
                  the original, so a converted score for these deals cannot be trusted. Investigate the flagged rows
                  (parity ✗) before applying.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {normReport.mode !== 'apply' && normReport.candidates > 0 && (
                <Button variant="primary" size="sm" loading={normApplying} onClick={handleNormRescoreApply}>
                  Apply rescore ({normReport.candidates})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setNormReport(null); setNormMessage(null); }}>
                Dismiss
              </Button>
            </div>
          </div>
          <div className="card-body">
            {(!normReport.rows || normReport.rows.length === 0) ? (
              <p className="text-muted" style={{ fontSize: 13 }}>No candidate deals to rescore.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Stage</th>
                      <th>Tier</th>
                      <th>Old Score</th>
                      <th>New Score</th>
                      <th>Old Passes</th>
                      <th>New Passes</th>
                      <th>Parity</th>
                      <th>Snapshot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {normReport.rows.map(row => (
                      <tr key={row.deal_id} style={row.parity_ok ? undefined : { background: 'var(--color-error-bg)' }}>
                        <td>{row.company_name || row.deal_id}</td>
                        <td>{formatStageLabel(row.current_stage)}</td>
                        <td>
                          <span className={row.tier === 'overwrite' ? 'badge badge-warning' : 'badge badge-info'}>
                            {row.tier === 'overwrite' ? 'Overwrite' : 'Protect + flag'}
                          </span>
                        </td>
                        <td>{row.old_normalised_score ?? '—'}</td>
                        <td style={{ fontWeight: 700 }}>{row.new_normalised_score ?? '—'}</td>
                        <td>{row.old_meets_threshold == null ? '—' : row.old_meets_threshold ? '✓' : '✗'}</td>
                        <td>{row.new_meets_threshold == null ? '—' : row.new_meets_threshold ? '✓' : '✗'}</td>
                        <td title={`face-value recompute: ${row.face_value_recomputed_score}`}>
                          {row.parity_ok
                            ? <span style={{ color: 'var(--color-success)' }}>✓</span>
                            : <span style={{ color: 'var(--color-error)', fontWeight: 700 }}>✗ {row.face_value_recomputed_score}</span>}
                        </td>
                        <td>{row.snapshot_exists ? 'exists' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '8px 0 0', margin: 0 }}>
              <strong>Overwrite:</strong> deals before HoP Review — score replaced (a one-time snapshot is preserved).{' '}
              <strong>Protect + flag:</strong> HoP Review or later — live score untouched; converted score stored
              alongside and the deal flagged <code>currency_rescore_pending_review</code> for a human to review.
            </p>
          </div>
        </div>
      )}

      {view === 'list' && (
        <FundList
          key={refreshKey}
          onAdd={() => setView('add')}
          onEdit={handleEdit}
          onView={handleView}
        />
      )}

      {(view === 'add' || view === 'edit') && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <Button variant="ghost" onClick={() => { setView('list'); setSelectedFund(null); }}>
              ← Back to Funds
            </Button>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
            {view === 'add' ? 'Add New Fund' : `Edit: ${selectedFund?.name}`}
          </h2>
          <FundForm
            fund={view === 'edit' ? selectedFund : null}
            onSaved={handleSaved}
            onCancel={() => { setView('list'); setSelectedFund(null); }}
          />
        </div>
      )}

      {/* Fund View / Version History Modal */}
      <Modal
        isOpen={viewHistoryOpen}
        onClose={() => { setViewHistoryOpen(false); setSelectedFund(null); }}
        title={`${selectedFund?.name} — Details`}
        size="lg"
      >
        {selectedFund && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
              {[
                ['Fund Name', selectedFund.name],
                ['Short Name', selectedFund.short_name],
                ['Vintage Year', selectedFund.vintage_year],
                ['Fund Life (years)', selectedFund.fund_life_years],
                ['Base Currency', selectedFund.base_currency],
                ['Fund Size', selectedFund.fund_size?.toLocaleString()],
                ['Status', selectedFund.status],
                ['Has Streams', selectedFund.has_streams ? 'Yes' : 'No'],
                ['Version', `v${selectedFund.config_version || 1}`],
              ].map(([label, value]) => (
                <div key={label}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</p>
                  <p style={{ fontSize: 14 }}>{value || '—'}</p>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>Active Rubric</p>
              <FundActiveRubric fundId={selectedFund.id} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <FundSubmissionUrls fund={selectedFund} />
            </div>

            {(selectedFund.config_history || []).length > 0 && (
              <VersionHistoryList history={selectedFund.config_history} />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
